// @ts-nocheck
import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Video, AlertCircle, ArrowLeft } from 'lucide-react'
import { PublicConductForm } from '@/components/PublicConductForm'
import { api } from '@/lib/api'

interface Participant {
  id: string
  firstName: string
  lastName: string
  email: string
  avatar?: string
  jobTitle?: string
  fullName?: string
}

interface SessionData {
  id: string
  title: string
  workflowType: string
  scheduledDate?: string
  duration?: number
  manager: Participant
  employee: Participant
  team?: {
    id: string
    name: string
    description?: string
  } | null
  questions?: any[]
  existingResponses?: any[]
  existingRatings?: {
    mood_rating?: number
    productivity_rating?: number
    temperature_rating?: number
  } | null
  existingActionItems?: any[]
  existingAchievements?: any[]
  notes?: string | null
  sessionExists?: boolean
  isCompleted?: boolean
  completedAt?: string | null
  token: string
  isPublic: boolean
}

interface ConductResponse {
  success: boolean
  data?: SessionData
  error?: string
}

export default function PublicConductPage() {
  const { token } = useParams<{ token: string }>()
  const [sessionData, setSessionData] = useState<SessionData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!token) {
      setError('Invalid conduct session link')
      setLoading(false)
      return
    }

    const fetchSession = async () => {
      try {
        setLoading(true)
        const result = await api.get<ConductResponse>(`/conduct/${token}`)
        if (!result.success || !result.data) {
          setError(result.error || 'Failed to load conduct session')
          return
        }

        setSessionData(result.data)
      } catch (err) {
        console.error('Error fetching conduct session data:', err)
        setError('Failed to load conduct session')
      } finally {
        setLoading(false)
      }
    }

    fetchSession()
  }, [token])

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-muted/30 via-background to-muted/30 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-lg text-muted-foreground">Loading 1-on-1 session...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-muted/30 via-background to-muted/30 flex items-center justify-center">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="pt-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-destructive" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">Access Error</h2>
              <p className="text-muted-foreground mb-6">{error}</p>

              {error.includes('not publicly accessible') && (
                <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 mb-4">
                  <p className="text-sm text-warning">
                    This 1-on-1 conduct session is not publicly accessible. Please contact the session organizer for access.
                  </p>
                </div>
              )}

              {error.includes('not found') && (
                <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-4">
                  <p className="text-sm text-destructive">
                    The session information could not be found. The link may be expired or invalid.
                  </p>
                </div>
              )}

              <Button onClick={() => window.location.reload()} variant="outline">
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!sessionData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-muted/30 via-background to-muted/30 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-foreground mb-2">No session data available</h2>
          <p className="text-muted-foreground">Unable to load 1-on-1 conduct session</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-muted/30 via-background to-muted/30">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="mb-6">
          {sessionData.isPublic ? (
            <button
              onClick={() => window.history.back()}
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors group"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              Go Back
            </button>
          ) : (
            <Link to="/dashboard" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors group">
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              Return to Dashboard
            </Link>
          )}
        </div>

        <div className="mb-8">
          <div className="text-center mb-6">
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-primary/5 to-accent/5 rounded-3xl flex items-center justify-center shadow-clean">
                <Video className="h-8 w-8 text-primary" />
              </div>
              <div>
                <h1 className="text-4xl font-bold text-foreground tracking-tight">Physical 1-on-1 Meeting</h1>
                <p className="text-xl text-muted-foreground">
                  Work through questions together and record responses in real-time
                </p>
              </div>
            </div>
          </div>
        </div>

        <PublicConductForm sessionData={sessionData} />
      </div>
    </div>
  )
}
